import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { useLocationMaster } from '../../../hooks/useLocationMaster';
import StorageService from '../../../services/StorageService';
import '../../../assets/styles/sample_request.scss';

export default function SampleRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  
  // Master Data Hooks
  const { attributeUnitOptions } = useProductDetailsMaster();
  const { 
    countries, 
    states, 
    cities, 
    loading: locationLoading, 
    loadingStates, 
    loadingCities,
    updateListsForCountry,
    updateListsForState
  } = useLocationMaster();
  
  // Get quotation ID from location state or URL params
  const quotationId = location.state?.quotationId || new URLSearchParams(location.search).get('quotationId');
  
  // State for form fields
  const [formData, setFormData] = useState({
    companyName: '',
    contactPersonName: '',
    email: '',
    phone: '',
    productQuantity: '',
    unit: '',
    samplePurpose: '',
    urgencyLevel: '',
    deliveryPreference: '',
    destinationAddress: '', // For display only
    // Address fields - stored directly in sample_details
    fullName: '',
    phoneNumber: '',
    addressLine1: '',
    addressLine2: '',
    countryId: null,
    stateId: null,
    cityId: null,
    pincode: '',
    tagId: null,
    expectedDeliveryDate: '',
    additionalNotes: ''
  });
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [productData, setProductData] = useState({
    name: '',
    price: 0,
    currency: 'INR',
    quantity: 0,
    unit: '',
    images: [],
    description: ''
  });
  const [addressData, setAddressData] = useState({
    fullName: '',
    phoneNumber: '',
    addressLine1: '',
    addressLine2: '',
    country: '',
    state: '',
    city: '',
    postalCode: '',
    tag: '',
    setAsDefault: false
  });

  // Fetch quotation data
  useEffect(() => {
    const fetchQuotationData = async () => {
      if (!quotationId) {
        toast.error('Quotation ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await CommonService.get(`/quotation/${quotationId}`);
        
        if (response && response.success && response.data) {
          const quotation = response.data;
          
          // Get first line item for product details
          const firstItem = quotation.items && quotation.items.length > 0 ? quotation.items[0] : null;
          
          // Parse attachments - could be string (JSON) or array
          let images = [];
          if (quotation.attachments) {
            if (typeof quotation.attachments === 'string') {
              try {
                const parsed = JSON.parse(quotation.attachments);
                images = Array.isArray(parsed) ? parsed : [parsed];
              } catch (e) {
                // If parsing fails, treat as single path string
                images = quotation.attachments ? [quotation.attachments] : [];
              }
            } else if (Array.isArray(quotation.attachments)) {
              images = quotation.attachments;
            } else if (typeof quotation.attachments === 'object') {
              // If it's an object, try to extract path
              images = quotation.attachments.path ? [quotation.attachments.path] : [quotation.attachments];
            }
          }
          
          // Filter out empty/null values and ensure we have valid paths
          images = images.filter(img => {
            if (!img) return false;
            if (typeof img === 'string') return img.trim().length > 0;
            if (typeof img === 'object' && img.path) return img.path.trim().length > 0;
            return false;
          });
          
          // If no images from quotation attachments, try to fetch RFQ product images
          if (images.length === 0 && quotation.rfq_id) {
            try {
              const rfqResponse = await CommonService.get(`/rfq/${quotation.rfq_id}`);
              if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                const rfq = rfqResponse.data;
                // RFQ product images are typically in product_images field or attachments
                if (rfq.product_images) {
                  const rfqImages = Array.isArray(rfq.product_images) 
                    ? rfq.product_images 
                    : (typeof rfq.product_images === 'string' ? JSON.parse(rfq.product_images) : []);
                  images = rfqImages.filter(img => img && (typeof img === 'string' || (typeof img === 'object' && img.path)));
                } else if (rfq.attachments) {
                  const rfqAttachments = Array.isArray(rfq.attachments) 
                    ? rfq.attachments 
                    : (typeof rfq.attachments === 'string' ? JSON.parse(rfq.attachments) : []);
                  images = rfqAttachments.filter(img => img && (typeof img === 'string' || (typeof img === 'object' && img.path)));
                }
              }
            } catch (rfqError) {
              console.warn('Could not fetch RFQ images:', rfqError);
            }
          }
          
          // Debug: Log attachments to see what we're getting
          console.log('Quotation attachments (raw):', quotation.attachments);
          console.log('Quotation attachments (type):', typeof quotation.attachments);
          console.log('Parsed images:', images);
          console.log('Images count:', images.length);
          
          // Log each image URL that will be generated (getFileUrl is defined later, so we'll log in render)
          console.log('Images to be displayed:', images.map((img, idx) => ({
            index: idx,
            original: img,
            type: typeof img
          })));
          
          // Set product data from quotation
          setProductData({
            name: firstItem?.description || quotation.product_name || 'Product',
            price: firstItem?.unit_price || 0,
            currency: quotation.currency || 'INR',
            quantity: firstItem?.quantity || 0,
            unit: firstItem?.uom || '',
            images: images,
            description: firstItem?.description || quotation.product_description || ''
          });

          // Auto-populate form with quotation data
          if (firstItem) {
            // Map UOM to unit value (could be ID or label)
            let unitValue = firstItem.uom || '';
            
            // If UOM is a label, try to find the corresponding value (ID)
            if (attributeUnitOptions && attributeUnitOptions.length > 0) {
              const foundUnit = attributeUnitOptions.find(opt => 
                opt.label === unitValue || 
                opt.label?.toLowerCase() === unitValue?.toLowerCase() ||
                String(opt.value) === String(unitValue)
              );
              if (foundUnit) {
                unitValue = foundUnit.value;
              }
            }
            
            setFormData(prev => ({
              ...prev,
              productQuantity: firstItem.quantity?.toString() || '',
              unit: unitValue
            }));
          }
        } else {
          toast.error('Failed to fetch quotation data');
        }
      } catch (error) {
        console.error('Error fetching quotation:', error);
        toast.error('Failed to load quotation data');
      } finally {
        setLoading(false);
      }
    };

    fetchQuotationData();
  }, [quotationId]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload attachments
  const uploadAttachments = async () => {
    const uploadedFiles = [];
    
    if (selectedFiles.length === 0) {
      return uploadedFiles;
    }

    // Get company name from user or form data - ensure it's not empty
    const currentUser = StorageService.getUser();
    let companyName = formData.companyName?.trim() || currentUser?.company_name || currentUser?.companyName;
    
    // If still empty, use a default that will be sanitized by backend
    if (!companyName || companyName === '') {
      companyName = 'Sample_Request';
    }
    
    console.log('Uploading files with company name:', companyName, {
      fromForm: formData.companyName,
      fromUser: currentUser?.company_name || currentUser?.companyName,
      final: companyName
    });

    for (const file of selectedFiles) {
      try {
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        // Use 'company' category (valid category) for sample request attachments
        // The backend validator only allows: 'company', 'factory', 'product', 'quality', 'clarification', 'quotation'
        formDataObj.append('category', 'company');
        formDataObj.append('companyName', companyName);
        formDataObj.append('fileType', 'sample_request_attachment');

        console.log('Uploading file:', file.name, {
          category: 'sample_request',
          companyName: companyName,
          fileType: 'sample_request_attachment',
          fileSize: file.size,
          fileType_mime: file.type
        });

        // Don't set Content-Type header - let browser set it with boundary
        const uploadResponse = await CommonService.post('/upload/single', formDataObj);
        
        console.log('Upload response:', uploadResponse);
        
        if (uploadResponse && uploadResponse.success && uploadResponse.data) {
          const accessPath = uploadResponse.data.accessPath || uploadResponse.data.filePath || uploadResponse.data.path || '';
          if (accessPath) {
            uploadedFiles.push(accessPath);
            console.log('File uploaded successfully:', accessPath);
          } else {
            console.warn('Upload response missing file path:', uploadResponse);
            throw new Error('Upload response missing file path');
          }
        } else {
          console.error('Upload failed - invalid response:', uploadResponse);
          const errorMsg = uploadResponse?.error || uploadResponse?.message || 'Upload failed';
          throw new Error(errorMsg);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        console.error('Error details:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          requestUrl: error?.config?.url,
          requestMethod: error?.config?.method
        });
        const errorMessage = error?.response?.data?.error || 
                            error?.response?.data?.message || 
                            error?.message || 
                            'Unknown error';
        toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
        throw error; // Re-throw to stop submission if upload fails
      }
    }

    return uploadedFiles;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.productQuantity || !formData.unit) {
      toast.error('Please enter product quantity and unit');
      return;
    }
    
    if (!formData.samplePurpose) {
      toast.error('Please select sample purpose');
      return;
    }
    
    if (!formData.urgencyLevel) {
      toast.error('Please select urgency level');
      return;
    }
    
    if (!formData.deliveryPreference) {
      toast.error('Please select delivery preference');
      return;
    }
    
    if (!formData.destinationAddress) {
      toast.error('Please enter destination address');
      return;
    }
    
    if (!formData.expectedDeliveryDate) {
      toast.error('Please select expected delivery date');
      return;
    }
    
    if (!formData.companyName || !formData.contactPersonName || !formData.email || !formData.phone) {
      toast.error('Please fill in all contact information fields');
      return;
    }

    try {
      setSubmitting(true);

      // Upload files first (only if files are selected)
      let uploadedAttachments = [];
      if (selectedFiles.length > 0) {
        try {
          uploadedAttachments = await uploadAttachments();
          console.log('Uploaded attachments:', uploadedAttachments);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          // Don't proceed if upload fails
          throw new Error(`File upload failed: ${uploadError?.response?.data?.error || uploadError?.message || 'Unknown error'}`);
        }
      }

      // Map delivery preference to ID (you may need to adjust this based on your master data)
      const deliveryPreferenceMap = {
        'standard': 1,
        'express': 2,
        'overnight': 3
      };

      // Map unit to quantity_type_id using attributeUnitOptions
      let quantityTypeId = null;
      if (formData.unit && attributeUnitOptions && attributeUnitOptions.length > 0) {
        const foundUnit = attributeUnitOptions.find(opt => 
          String(opt.value) === String(formData.unit)
        );
        if (foundUnit) {
          quantityTypeId = foundUnit.value;
        }
      }

      // Prepare payload
      const payload = {
        quotation_id: quotationId ? parseInt(quotationId) : null,
        quantity: parseInt(formData.productQuantity) || 0,
        quantity_type_id: quantityTypeId,
        total_amount: productData.price * (parseInt(formData.productQuantity) || 0),
        delivery_preference_id: deliveryPreferenceMap[formData.deliveryPreference] || null,
        delivery_address_id: null, // Not used - address stored directly in sample_details
        exp_dispatch_date: formData.expectedDeliveryDate,
        add_notes: formData.additionalNotes,
        sample_purpose: formData.samplePurpose,
        urgency_level: formData.urgencyLevel,
        company_name: formData.companyName,
        contact_person_name: formData.contactPersonName,
        email: formData.email,
        phone: formData.phone,
        destination_address: formData.destinationAddress, // For display/backward compatibility
        // Address fields - stored directly in sample_details table
        full_name: formData.fullName || null,
        phone_number: formData.phoneNumber || null,
        add_line1: formData.addressLine1 || null,
        add_line2: formData.addressLine2 || null,
        country_id: formData.countryId || null,
        state_id: formData.stateId || null,
        city_id: formData.cityId || null,
        pincode: formData.pincode || null,
        tag_id: formData.tagId || null,
        attachments: uploadedAttachments.length > 0 ? JSON.stringify(uploadedAttachments) : null,
        file_name: selectedFiles.length > 0 ? selectedFiles.map(f => f.name).join(', ') : null
      };

      console.log('Submitting sample request with payload:', payload);
      const response = await CommonService.post('/sample-request', payload);
      console.log('Sample request response:', response);

      if (response && response.success) {
        toast.success('Sample request submitted successfully!');
        // Navigate back to quotation list
        setTimeout(() => {
          navigate('/seller-quotation-list');
        }, 1500);
      } else {
        throw new Error(response?.error || 'Failed to submit sample request');
      }
    } catch (error) {
      console.error('Error submitting sample request:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        url: error?.config?.url,
        baseURL: error?.config?.baseURL
      });
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to submit sample request';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to get full file URL from attachment path
  const getFileUrl = (attachment) => {
    if (!attachment) {
      console.log('getFileUrl: No attachment provided');
      return null;
    }
    
    // Handle if attachment is an object with path, url, or name property
    let attachmentPath = null;
    if (typeof attachment === 'object') {
      attachmentPath = attachment.path || attachment.url || attachment.name || attachment.filePath || attachment.accessPath;
    } else {
      attachmentPath = attachment;
    }
    
    if (!attachmentPath) {
      console.log('getFileUrl: No attachment path found', attachment);
      return null;
    }
    
    // Convert to string if it's not already
    attachmentPath = String(attachmentPath).trim();
    
    if (!attachmentPath || attachmentPath === 'null' || attachmentPath === 'undefined') {
      console.log('getFileUrl: Empty or invalid path', attachmentPath);
      return null;
    }
    
    const baseUrl = 'http://localhost:3000';
    
    console.log('getFileUrl: Processing attachment path:', attachmentPath, 'from attachment:', attachment);
    
    // If already a full URL, return as is
    if (attachmentPath.startsWith('http://') || attachmentPath.startsWith('https://')) {
      console.log('getFileUrl: Already a full URL, returning as is');
      return attachmentPath;
    }
    
    // Check if it's already a relative path starting with /quotation-documents/
    // This check must come BEFORE checking for Windows paths to avoid double-pathing
    if (attachmentPath.startsWith('/quotation-documents/')) {
      const url = `${baseUrl}${attachmentPath}`;
      console.log('getFileUrl: Quotation document relative path URL:', url);
      return url;
    }
    
    // Check if it's a quotation document path (Windows path like C:\usr\local\b2b\seller\QuotationDocument\...)
    // Only process if it's NOT already a relative path
    if (attachmentPath.includes('QuotationDocument')) {
      // Extract the relative path after QuotationDocument
      const parts = attachmentPath.split('QuotationDocument');
      let relativePath = parts[parts.length - 1];
      // Clean up the path - remove leading slashes/backslashes and normalize
      relativePath = relativePath.replace(/^[\\\/]+/, '').replace(/\\/g, '/');
      const url = `${baseUrl}/quotation-documents/${relativePath}`;
      console.log('getFileUrl: Quotation document URL (from Windows path):', url);
      return url;
    }
    
    // Check if it's an RFQ image path (starts with /rfq-images/)
    if (attachmentPath.startsWith('/rfq-images/')) {
      const url = `${baseUrl}${attachmentPath}`;
      console.log('getFileUrl: RFQ image URL:', url);
      return url;
    }
    
    // Check if it's a media path
    if (attachmentPath.includes('media') || attachmentPath.includes('product') || attachmentPath.includes('company')) {
      // Ensure path starts with /
      const cleanPath = attachmentPath.startsWith('/') ? attachmentPath : '/' + attachmentPath;
      const url = `${baseUrl}${cleanPath}`;
      console.log('getFileUrl: Media path URL:', url);
      return url;
    }
    
    // Check if it's a relative path starting with /
    if (attachmentPath.startsWith('/')) {
      const url = `${baseUrl}${attachmentPath}`;
      console.log('getFileUrl: Relative path URL:', url);
      return url;
    }
    
    // If it's just a filename, try to construct a path
    // Check if it looks like a quotation document filename
    if (attachmentPath.includes('quotation') || attachmentPath.match(/^[a-zA-Z0-9_-]+\.(jpg|jpeg|png|gif|webp|pdf)$/i)) {
      // Try quotation-documents first
      const url = `${baseUrl}/quotation-documents/${attachmentPath}`;
      console.log('getFileUrl: Constructed quotation document URL from filename:', url);
      return url;
    }
    
    // Default: assume it's a media file
    const cleanPath = attachmentPath.startsWith('/') ? attachmentPath : '/' + attachmentPath;
    const url = `${baseUrl}/media/${cleanPath.replace(/^\/+/, '')}`;
    console.log('getFileUrl: Default media URL:', url);
    return url;
  };

  // Helper function to get unit label from value (ID or label)
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return 'Units';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    // Convert both to numbers for comparison if possible
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    
    // First try to find by value (ID) - handle both string and numeric comparison
    const foundByValue = attributeUnitOptions.find(opt => {
      // Try exact string match first
      if (String(opt.value) === String(unitValue)) return true;
      // Try numeric match if both are numbers
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    // If not found by ID, try to find by label (might already be a label)
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="sample-request">
        <div className="sr-main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="sample-request">
      <div className="sr-main-content">
        {/* Header Section */}
        <div className="sr-header">
          <div className="sr-header-left">
            <nav aria-label="breadcrumb" className="sr-breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a 
                    href="#" 
                    className="breadcrumb-link" 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/seller-quotation-list');
                    }}
                  >
                    Quotations
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <a 
                    href="#" 
                    className="breadcrumb-link" 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/seller-quotation-accept');
                    }}
                  >
                    Quotation Details
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Sample Request
                </li>
              </ol>
            </nav>
            <h1 className="sr-main-title">Sample Request</h1>
          </div>
          <button className="sr-back-btn" onClick={() => navigate(-1)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            Back
          </button>
        </div>

        {/* Main Content - Two Columns */}
        <div className="sr-content-grid">
          {/* Left Column - Product Information */}
          <div className="sr-left-column">
            {/* Left Top - Electronic Product Images Card */}
            <div className="sr-card">
              <div className="sr-card-header">
                <h2 className="sr-card-title">Electronic Product</h2>
                <p className="sr-card-subtitle">View detailed product image and specifications</p>
              </div>
              <div className="sr-card-content">
                {/* Product Image Gallery */}
                <div className="sr-image-gallery">
                  {productData.images && productData.images.length > 0 ? (
                    <>
                      <div className="sr-thumbnails">
                        {productData.images.map((image, index) => {
                          const imageUrl = getFileUrl(image);
                          console.log(`Rendering thumbnail ${index}:`, { image, imageUrl });
                          return (
                            <div
                              key={index}
                              className={`sr-thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                              onClick={() => setSelectedImageIndex(index)}
                            >
                              <img 
                                src={imageUrl || ''} 
                                alt={`Product view ${index + 1}`}
                                onError={(e) => {
                                  console.error('Thumbnail image load error:', {
                                    image,
                                    imageUrl,
                                    attemptedUrl: e.target.src,
                                    error: 'Failed to load image'
                                  });
                                  e.target.style.display = 'none';
                                }}
                                onLoad={() => {
                                  console.log('Thumbnail image loaded successfully:', imageUrl);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="sr-main-image">
                        {productData.images[selectedImageIndex] ? (
                          (() => {
                            const mainImageUrl = getFileUrl(productData.images[selectedImageIndex]);
                            console.log('Rendering main image:', {
                              image: productData.images[selectedImageIndex],
                              imageUrl: mainImageUrl,
                              selectedIndex: selectedImageIndex
                            });
                            return (
                              <img 
                                src={mainImageUrl || ''} 
                                alt={productData.name}
                                onError={(e) => {
                                  console.error('Main image load error:', {
                                    image: productData.images[selectedImageIndex],
                                    imageUrl: mainImageUrl,
                                    attemptedUrl: e.target.src,
                                    error: 'Failed to load image'
                                  });
                                  e.target.style.display = 'none';
                                }}
                                onLoad={() => {
                                  console.log('Main image loaded successfully:', mainImageUrl);
                                }}
                              />
                            );
                          })()
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f5f5f5', color: '#999' }}>
                            <div style={{ textAlign: 'center' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '8px' }}>
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                              </svg>
                              <p>No image available</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="sr-main-image">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f5f5f5', color: '#999' }}>
                        <div style={{ textAlign: 'center' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '8px' }}>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                          <p>No images available</p>
                          <p style={{ fontSize: '12px', marginTop: '8px', color: '#999' }}>
                            {productData.images ? `Images array length: ${productData.images.length}` : 'No images array'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Left Bottom - Description Card */}
            <div className="sr-card">
              <div className="sr-card-header">
                <h2 className="sr-card-title">Description</h2>
              </div>
              <div className="sr-card-content">
                <p className="sr-description-text">{productData.description}</p>
              </div>
            </div>
          </div>

          {/* Right Column - Sample Details and Contact Information */}
          <div className="sr-right-column">
            {/* Sample Details Card */}
            <div className="sr-card">
              <div className="sr-card-header">
                <h2 className="sr-card-title">Sample Details</h2>
              </div>
              <div className="sr-card-content">
                {/* Product Quantity, Unit, and Price in same row */}
                <div className="sr-quantity-unit-price-row">
                  {/* Product Quantity */}
                  <div className="sr-form-group">
                    <input
                      type="text"
                      name="productQuantity"
                      className="sr-input"
                      placeholder="Enter Product Quantity"
                      value={formData.productQuantity}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Unit */}
                  <div className="sr-form-group">
                    <select
                      name="unit"
                      className="sr-input sr-select"
                      value={formData.unit}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Unit</option>
                      {attributeUnitOptions && attributeUnitOptions.length > 0 ? (
                        attributeUnitOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="piece">Piece</option>
                          <option value="kg">Kg</option>
                          <option value="gram">Gram</option>
                          <option value="liter">Liter</option>
                          <option value="meter">Meter</option>
                          <option value="box">Box</option>
                          <option value="pack">Pack</option>
                        </>
                      )}
                    </select>
                  </div>

                  {/* Price Display */}
                  <div className="sr-price-display">
                    <span className="sr-price-label">INR (₹)</span>
                    <span className="sr-price-value">{productData.price.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {/* Sample Purpose */}
                <div className="sr-form-group">
                  <select
                    name="samplePurpose"
                    className="sr-input sr-select"
                    value={formData.samplePurpose}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Sample Purpose</option>
                    <option value="quality-check">Quality Check</option>
                    <option value="testing">Testing</option>
                    <option value="evaluation">Evaluation</option>
                    <option value="marketing">Marketing</option>
                    <option value="customization">Customization Review</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Urgency Level */}
                <div className="sr-form-group">
                  <select
                    name="urgencyLevel"
                    className="sr-input sr-select"
                    value={formData.urgencyLevel}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Urgency Level</option>
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent</option>
                    <option value="rush">Rush</option>
                  </select>
                </div>

                {/* File Upload */}
                <div className="sr-file-upload-section">
                  <div className="sr-file-upload-box" onClick={handleFileClick}>
                    <div className="sr-file-upload-content">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sr-upload-icon">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      <div className="sr-file-upload-text">
                        <p className="sr-file-upload-title">Attach Supporting Files</p>
                        <p className="sr-file-upload-desc">Attach All relevant files that help the supplier better understand you customization requirements.</p>
                      </div>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      multiple
                      style={{ display: 'none' }}
                    />
                    <button type="button" className="sr-choose-file-btn">Choose File</button>
                  </div>
                  
                  {/* Selected Files List */}
                  {selectedFiles.length > 0 && (
                    <div className="sr-selected-files">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="sr-file-item">
                          <span className="sr-file-name">{file.name}</span>
                          <button
                            type="button"
                            className="sr-file-remove"
                            onClick={() => handleRemoveFile(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information Card */}
            <div className="sr-card">
              <div className="sr-card-header">
                <h2 className="sr-card-title">Contact Information</h2>
              </div>
              <div className="sr-card-content">
                <div className="sr-contact-grid">
                  {/* Company Name */}
                  <div className="sr-form-group">
                    <input
                      type="text"
                      name="companyName"
                      className="sr-input"
                      placeholder="Company Name"
                      value={formData.companyName}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Contact Person Name */}
                  <div className="sr-form-group">
                    <input
                      type="text"
                      name="contactPersonName"
                      className="sr-input"
                      placeholder="Contact Person Name"
                      value={formData.contactPersonName}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Email */}
                  <div className="sr-form-group">
                    <input
                      type="email"
                      name="email"
                      className="sr-input"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Phone */}
                  <div className="sr-form-group">
                    <input
                      type="tel"
                      name="phone"
                      className="sr-input"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Full Width Section - Shipping Details */}
        <div className="sr-full-width-section">
          <div className="sr-full-width-grid">
            {/* Shipping Details Card */}
            <div className="sr-card">
              <div className="sr-card-header">
                <h2 className="sr-card-title">Shipping Details</h2>
              </div>
              <div className="sr-card-content">
                {/* Delivery Preference, Destination Address, and Date in same row */}
                <div className="sr-shipping-row">
                  <div className="sr-form-group">
                    <select
                      name="deliveryPreference"
                      className="sr-input sr-select"
                      value={formData.deliveryPreference}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Your Delivery Preference</option>
                      <option value="standard">Standard Delivery</option>
                      <option value="express">Express Delivery</option>
                      <option value="overnight">Overnight Delivery</option>
                    </select>
                  </div>

                  <div className="sr-form-group">
                    <div className="sr-address-input-wrapper">
                      <input
                        type="text"
                        name="destinationAddress"
                        className="sr-input sr-address-input"
                        placeholder="Enter the Destination Address"
                        value={formData.destinationAddress}
                        onChange={handleInputChange}
                      />
                      <button 
                        type="button" 
                        className="sr-add-address-btn"
                        onClick={() => setShowAddressModal(true)}
                      >
                        Add Address
                      </button>
                    </div>
                  </div>

                  <div className="sr-form-group">
                    <input
                      type="date"
                      name="expectedDeliveryDate"
                      className="sr-input sr-date-input"
                      placeholder="Expected Delivery Date"
                      value={formData.expectedDeliveryDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="sr-form-group">
                  <textarea
                    name="additionalNotes"
                    className="sr-textarea"
                    placeholder="Additional Notes"
                    value={formData.additionalNotes}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="sr-submit-section">
          <button 
            type="button" 
            className="sr-submit-btn" 
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>

      {/* Add Address Modal */}
      {showAddressModal && (
        <div className="sr-modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="sr-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="sr-modal-header">
              <h2 className="sr-modal-title">Add Address</h2>
              <button 
                className="sr-modal-close" 
                onClick={() => setShowAddressModal(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="sr-modal-body">
              <div className="sr-modal-form-row">
                <div className="sr-modal-form-group">
                  <input
                    type="text"
                    className="sr-modal-input"
                    placeholder="Full Name"
                    value={addressData.fullName}
                    onChange={(e) => setAddressData({...addressData, fullName: e.target.value})}
                  />
                </div>
                <div className="sr-modal-form-group">
                  <input
                    type="text"
                    className="sr-modal-input"
                    placeholder="Phone Number"
                    value={addressData.phoneNumber}
                    onChange={(e) => setAddressData({...addressData, phoneNumber: e.target.value})}
                  />
                </div>
              </div>

              <div className="sr-modal-form-group">
                <input
                  type="text"
                  className="sr-modal-input"
                  placeholder="Address Line1"
                  value={addressData.addressLine1}
                  onChange={(e) => setAddressData({...addressData, addressLine1: e.target.value})}
                />
              </div>

              <div className="sr-modal-form-group">
                <input
                  type="text"
                  className="sr-modal-input"
                  placeholder="Address Line2"
                  value={addressData.addressLine2}
                  onChange={(e) => setAddressData({...addressData, addressLine2: e.target.value})}
                />
              </div>

              <div className="sr-modal-form-row sr-modal-form-row-four">
                <div className="sr-modal-form-group">
                  <select
                    className="sr-modal-input sr-modal-select"
                    value={addressData.country}
                    onChange={async (e) => {
                      const countryId = e.target.value;
                      setAddressData({...addressData, country: countryId, state: '', city: ''});
                      // Fetch states for selected country
                      await updateListsForCountry(countryId);
                    }}
                    disabled={locationLoading}
                  >
                    <option value="">{locationLoading ? 'Loading...' : 'Country'}</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sr-modal-form-group">
                  <select
                    className="sr-modal-input sr-modal-select"
                    value={addressData.state}
                    onChange={async (e) => {
                      const stateId = e.target.value;
                      setAddressData({...addressData, state: stateId, city: ''});
                      // Fetch cities for selected state
                      await updateListsForState(addressData.country, stateId);
                    }}
                    disabled={!addressData.country || loadingStates}
                  >
                    <option value="">{loadingStates ? 'Loading...' : 'State'}</option>
                    {states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sr-modal-form-group">
                  <select
                    className="sr-modal-input sr-modal-select"
                    value={addressData.city}
                    onChange={(e) => setAddressData({...addressData, city: e.target.value})}
                    disabled={!addressData.state || loadingCities}
                  >
                    <option value="">{loadingCities ? 'Loading...' : 'City'}</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sr-modal-form-group">
                  <input
                    type="text"
                    className="sr-modal-input"
                    placeholder="Postal code"
                    value={addressData.postalCode}
                    onChange={(e) => setAddressData({...addressData, postalCode: e.target.value})}
                  />
                </div>
              </div>

              <div className="sr-modal-form-group">
                <select
                  className="sr-modal-input sr-modal-select"
                  value={addressData.tag}
                  onChange={(e) => setAddressData({...addressData, tag: e.target.value})}
                >
                  <option value="">Tag</option>
                  <option value="home">Home</option>
                  <option value="work">Work</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="sr-modal-checkbox-group">
                <label className="sr-modal-checkbox-label">
                  <input
                    type="checkbox"
                    className="sr-modal-checkbox"
                    checked={addressData.setAsDefault}
                    onChange={(e) => setAddressData({...addressData, setAsDefault: e.target.checked})}
                  />
                  <span>Set us default shipping address</span>
                </label>
              </div>
            </div>

            <div className="sr-modal-footer">
              <button 
                className="sr-modal-close-btn"
                onClick={() => setShowAddressModal(false)}
              >
                Close
              </button>
              <button 
                className="sr-modal-submit-btn"
                onClick={() => {
                  // Validate required fields
                  if (!addressData.addressLine1) {
                    toast.error('Please enter address line 1');
                    return;
                  }
                  if (!addressData.country) {
                    toast.error('Please select a country');
                    return;
                  }
                  if (!addressData.state) {
                    toast.error('Please select a state');
                    return;
                  }
                  if (!addressData.city) {
                    toast.error('Please select a city');
                    return;
                  }

                  // Get labels for country, state, and city for display
                  const countryLabel = countries.find(c => c.id === addressData.country)?.label || '';
                  const stateLabel = states.find(s => s.id === addressData.state)?.label || '';
                  const cityLabel = cities.find(c => c.id === addressData.city)?.label || '';
                  
                  // Build full address string for display
                  const addressParts = [];
                  if (addressData.addressLine1) addressParts.push(addressData.addressLine1);
                  if (addressData.addressLine2) addressParts.push(addressData.addressLine2);
                  if (cityLabel) addressParts.push(cityLabel);
                  if (stateLabel) addressParts.push(stateLabel);
                  if (addressData.postalCode) addressParts.push(addressData.postalCode);
                  if (countryLabel) addressParts.push(countryLabel);
                  
                  const fullAddress = addressParts.join(', ');
                  
                  // Update form data with all address fields
                  setFormData({
                    ...formData, 
                    destinationAddress: fullAddress,
                    fullName: addressData.fullName || formData.contactPersonName || '',
                    phoneNumber: addressData.phoneNumber || formData.phone || '',
                    addressLine1: addressData.addressLine1,
                    addressLine2: addressData.addressLine2 || '',
                    countryId: parseInt(addressData.country),
                    stateId: parseInt(addressData.state),
                    cityId: parseInt(addressData.city),
                    pincode: addressData.postalCode || '',
                    tagId: addressData.tag && !isNaN(parseInt(addressData.tag)) ? parseInt(addressData.tag) : null
                  });
                  
                  toast.success('Address added successfully');
                  setShowAddressModal(false);
                  
                  // Reset address form
                  setAddressData({
                    fullName: '',
                    phoneNumber: '',
                    addressLine1: '',
                    addressLine2: '',
                    country: '',
                    state: '',
                    city: '',
                    postalCode: '',
                    tag: '',
                    setAsDefault: false
                  });
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

